import { useEffect, useState } from "react";

import { currentAccessToken } from "@utils/viewAs";

import { useAppSelector } from "@/store/hooks";
import instance from "@/store/middleware/api";
import { isAuthorized } from "@/store/selectors/getMeSelector";

/**
 * Shared source of truth for the "Action Required" count shown in:
 *  - the main-nav "Claims" item badge
 *  - the AccountMenu dropdown row badge next to "Claims"
 *  - the Claims page Action Required tab pill
 *
 * Also exposes the in-page refresh banner's freshness count
 * (`actionRequiredSinceLastSeen`) — the server-computed number of
 * action-required cards whose latest status event landed after the
 * user's `lastClaimsPageSeenAt` watermark. See Phase 18b notes.
 *
 * The count is the live count from `/api/claims/my/counts`. The hook
 * keeps it fresh by:
 *
 *  - fetching on the first hook mount (anywhere on the site)
 *  - refetching on the global `claims-action-required-changed` window
 *    event — fired by LicensesTab after any user action that could move
 *    a claim into or out of the Action Required state (hide / unhide /
 *    submit / resolve / queue-for-next-cycle).
 *  - refetching on window focus (catches webhook-driven state changes
 *    that happened in another tab or while the user was away).
 *
 * Plus imperative `setActionRequiredCount(n)` for LicensesTab to push
 * the count synchronously after it computes locally — so the badge
 * updates before the background refetch confirms.
 */

type CountsState = { count: number; sinceLastSeen: number };

let currentState: CountsState = { count: 0, sinceLastSeen: 0 };
let inflightFetch: Promise<void> | null = null;
const subscribers = new Set<(s: CountsState) => void>();

const notify = (next: CountsState) => {
  if (next.count === currentState.count && next.sinceLastSeen === currentState.sinceLastSeen) return;
  currentState = next;
  subscribers.forEach((fn) => fn(next));
};

/** True when SOME credential (access or refresh token) is on disk — i.e. the
    user is logged in. We fire `/counts` only then: an unauthenticated call
    401s ("No token provided") and the silent catch below would pin the badge
    at 0 with no recovery on a static page (e.g. home). When only the refresh
    token survives, the api interceptor refreshes + retries so the call still
    resolves. Reads localStorage directly — this is module scope. */
const hasCredentials = (): boolean => {
  try {
    const read = (k: string) => {
      const raw = localStorage.getItem(k);
      return raw ? JSON.parse(raw)?.value : null;
    };
    return !!(read("accessToken") || read("refreshToken"));
  } catch {
    return false;
  }
};

const fetchFromServer = async (): Promise<void> => {
  if (!hasCredentials()) return;
  if (inflightFetch) return inflightFetch;
  inflightFetch = (async () => {
    try {
      const { data } = await instance.get<{
        actionRequired: number;
        underReview: number;
        resolved: number;
        actionRequiredSinceLastSeen?: number;
      }>("/claims/my/counts");
      if (typeof data?.actionRequired === "number") {
        notify({
          count: data.actionRequired,
          sinceLastSeen: typeof data.actionRequiredSinceLastSeen === "number"
            ? data.actionRequiredSinceLastSeen
            : 0,
        });
      }
    } catch {
      // Silently keep the cached value — could be unauthenticated, offline,
      // or backend down. Better to keep the last-known count than zero out
      // a real action-required state and hide it from the user.
    } finally {
      inflightFetch = null;
    }
  })();
  return inflightFetch;
};

/** Imperative setter — LicensesTab pushes the locally-computed count so
    the badge updates synchronously after a user action (hide / submit /
    resolve / etc) without waiting for the background refetch. Keeps the
    existing freshness count untouched. */
export const setActionRequiredCount = (n: number): void => {
  notify({ count: n, sinceLastSeen: currentState.sinceLastSeen });
};

/** Imperative refetch trigger — call from any action that may have
    changed the count from outside LicensesTab. Equivalent to dispatching
    the `claims-action-required-changed` window event. */
export const refetchActionRequiredCount = (): void => {
  void fetchFromServer();
};

export const getActionRequiredCount = (): number => currentState.count;

/** Locally zero the freshness count without a server round-trip. Used
    by the banner's Refresh handler so the banner can collapse instantly
    while the POST /claims/my/seen + subsequent /counts refetch resolves
    in the background. */
export const clearActionRequiredFreshness = (): void => {
  notify({ count: currentState.count, sinceLastSeen: 0 });
};

/** Bump the server-side watermark + refetch. Banner Refresh + the
    /account/claims auto-release toggle both call this. */
export const markClaimsPageSeen = async (): Promise<void> => {
  try {
    await instance.post("/claims/my/seen");
  } catch {
    // Best-effort — if the POST fails the next /counts call still works,
    // it just means the banner won't reset on this attempt.
  }
  void fetchFromServer();
};

/** Best-effort bump on page-hidden — uses `fetch` with `keepalive: true`
    so the request survives unload. Carries the Authorization header
    because the auth guard only accepts bearer tokens that way. Token
    is the wrapped `{ value: "…" }` shape that the api interceptor reads. */
export const markClaimsPageSeenBeacon = (): void => {
  try {
    const base = (instance.defaults.baseURL ?? "").replace(/\/$/, "");
    const raw = (() => {
      try { return currentAccessToken(); } catch { return null; }
    })();
    if (!raw) return;
    let token: string | null = null;
    try { token = JSON.parse(raw)?.value ?? null; } catch { /* malformed token cell */ }
    if (!token) return;
    void fetch(`${base}/claims/my/seen`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: "{}",
    });
  } catch {
    // Best-effort only — refresh+toggle are the primary bump paths.
  }
};

export const useActionRequiredCount = (): number => {
  const [value, setValue] = useState<number>(currentState.count);
  const loggedIn = useAppSelector(isAuthorized);
  useEffect(() => {
    const sub = (s: CountsState) => setValue(s.count);
    subscribers.add(sub);
    setValue(currentState.count);

    const onChanged = () => void fetchFromServer();
    const onFocus = () => void fetchFromServer();
    window.addEventListener("claims-action-required-changed", onChanged);
    window.addEventListener("focus", onFocus);

    return () => {
      subscribers.delete(sub);
      window.removeEventListener("claims-action-required-changed", onChanged);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Fetch keyed on auth-readiness rather than bare mount. The badge mounts
  // (SecondHeader / AccountMenu) before the boot session-restore lands, so a
  // mount-time fetch races ahead of the token and 401s — the silent catch then
  // leaves the badge stuck at 0 on a static page (home) until the user opens
  // Claims (where LicensesTab pushes the count). Keying on `isAuthorized`
  // fetches once credentials exist + refetches when auth flips on; logout zeroes
  // the shared count for the next user.
  useEffect(() => {
    if (loggedIn) void fetchFromServer();
    else notify({ count: 0, sinceLastSeen: 0 });
  }, [loggedIn]);

  return value;
};

/** Subscribe to the server-computed freshness count for the in-page
    refresh banner. Same singleton/refetch wiring as
    `useActionRequiredCount` — they share state. */
export const useActionRequiredSinceLastSeen = (): number => {
  const [value, setValue] = useState<number>(currentState.sinceLastSeen);
  useEffect(() => {
    const sub = (s: CountsState) => setValue(s.sinceLastSeen);
    subscribers.add(sub);
    setValue(currentState.sinceLastSeen);
    void fetchFromServer();
    return () => {
      subscribers.delete(sub);
    };
  }, []);
  return value;
};
